import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCreatePostMutation } from "@/features/api/postApi";

import RichTextEditor from "@/components/ui/RichTextEditor";

const CreatePost = () => {
  const [input, setInput] = useState({
    title: "",
    slug: "",
    metaDescription: "",
    content: "",
    altText: "",
    featuredImage: null,
    status: "draft",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [createPost, { isLoading }] = useCreatePostMutation();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "slug") {
      updatedValue = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    setInput((prev) => ({ ...prev, [name]: updatedValue }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image must be under 2MB.");
      return;
    }

    setInput((prev) => ({ ...prev, featuredImage: file }));

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleEditorChange = (newContent) => {
    setInput((prev) => ({ ...prev, content: newContent }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { title, slug, content } = input;
    if (!title.trim() || !slug.trim() || !content.trim()) {
      toast.error("Title, slug, and content are required!");
      return;
    }

    const formData = new FormData();
    Object.entries(input).forEach(([key, value]) => {
      if (key === "featuredImage" && value instanceof File) {
        formData.append(key, value);
      } else if (value) {
        formData.append(key, value);
      }
    });

    try {
      await createPost(formData).unwrap();
      toast.success("Post created successfully!");
      navigate("/admin/posts");
    } catch (err) {
      console.error("Create Post Error:", err);
      toast.error(err?.data?.message || "Failed to create post.");
    }
  };

  const config = {
    readonly: false,
    placeholder: "Start typing...",
    height: 400,
  };

  return (
    <div className="flex-1 p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">📄 Create a New Post</h1>
        <Button
          variant="outline"
          onClick={() => navigate("/admin/posts")}
          aria-label="Back to Posts"
          className="hover:bg-blue-600 hover:text-white"
        >
          Back to Posts →
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <CardDescription>
              Fill in the details to create a new post
            </CardDescription>
            {input.slug && (
              <Button
                variant="outline"
                type="button"
                onClick={() => window.open(`/post/${input.slug}`, "_blank")}
              >
                Preview
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                name="title"
                value={input.title}
                onChange={handleChange}
                placeholder="Enter post title"
              />
            </div>

            <div>
              <Label htmlFor="metaDescription">Meta Description</Label>
              <Input
                id="metaDescription"
                name="metaDescription"
                value={input.metaDescription}
                onChange={handleChange}
                placeholder="Short description for SEO"
              />
            </div>

            <div>
              <Label htmlFor="slug">Slug (URL)</Label>
              <Input
                id="slug"
                name="slug"
                value={input.slug}
                onChange={handleChange}
                placeholder="e.g. about-us"
              />
            </div>

            <div>
              <Label htmlFor="content">Content</Label>
              <RichTextEditor
                value={input.content}
                config={config}
                onChange={handleEditorChange}
              />
            </div>

            <div>
              <Label htmlFor="featuredImage">Featured Image</Label>
              <Input
                id="featuredImage"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mt-2 w-40 h-auto rounded-md border"
                />
              )}
            </div>

            <div>
              <Label htmlFor="altText">Alt Text</Label>
              <Input
                id="altText"
                name="altText"
                value={input.altText}
                onChange={handleChange}
                placeholder="Image alt text"
              />
            </div>

            <div className="pt-2">
              <Label>Status</Label>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="draft"
                    name="status"
                    value="draft"
                    checked={input.status === "draft"}
                    onChange={handleChange}
                  />
                  <Label htmlFor="draft">Draft</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="published"
                    name="status"
                    value="published"
                    checked={input.status === "published"}
                    onChange={handleChange}
                  />
                  <Label htmlFor="published">Published</Label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/admin/posts")}
              >
                Back to Posts
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Post"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatePost;
